import React, { useMemo } from 'react';
import moment from 'moment/moment';
import cn from '@/utils/cn';
import I18n from '@/modules/i18n';
import Icon from '@/components/ui/Icon';
import Tooltip from '@/components/ui/Tooltip';
import { ISubmission } from '@/components/interfaces';
import { IAssignment, StatusType } from '../../types';
import useGradesTableStore from '../../store/useGradesTableStore';

interface StatusIndicatorIconProps {
  className?: string;
  type: StatusType;
}

export const STATUS_INFO: Record<StatusType, {
  color: string;
  label: string;
}> = {
  inProgress: { color: '#ffca17', label: I18n.t('in_progress') },
  lowScore: { color: '#d32c23', label: I18n.t('low_score') },
  notAssigned: { color: '#555555', label: 'Not assigned' },
  notStarted: { color: 'transparent', label: I18n.t('not_started') },
  submitted: { color: '#78962d', label: I18n.t('turned_in') },
};

const CIRCLE_SIZE = 10;
const SKIP_SIZE = 14;

export const StatusIndicatorIcon = ({ className, type }: StatusIndicatorIconProps) => {
  if (type === 'notStarted') return null;

  const size = type === 'notAssigned' ? SKIP_SIZE : CIRCLE_SIZE;
  const { color, label } = STATUS_INFO[type];

  return (
    <span
      aria-label={label}
      className={cn('tw-flex tw-items-center tw-justify-center', className)}
      role="img"
      style={{
        height: size,
        width: size,
      }}
    >
      <Icon
        color={color}
        icon={type === 'notAssigned' ? 'Skip' : 'Circle'}
      />
    </span>
  );
};

interface StatusIndicatorProps {
  assignment: IAssignment;
  isAssigned?: boolean;
  submission: ISubmission;
}

const StatusIndicator = ({ assignment, isAssigned, submission }: StatusIndicatorProps) => {
  const lowScoresSettings = useGradesTableStore(state => state.lowScoresSettings);

  const grade = submission?.total_score_override ?? submission?.total_score;

  const isLowScore = () => {
    if (!lowScoresSettings.enabled) return false;
    if (!grade) return false;

    return grade < parseFloat(assignment.max_points) * (lowScoresSettings.threshold / 100.0);
  };

  const status = useMemo<StatusType>(() => {
    if (!isAssigned) return 'notAssigned';
    if (!submission) return 'notStarted';
    if (submission.submitted_at && isLowScore()) return 'lowScore';
    if (submission.submitted_at) return 'submitted';

    return 'inProgress';
  }, [isAssigned, submission]);

  const isEmptyType = ['notStarted', 'notAssigned'].includes(status);

  const formatTime = (timeStamp: string | undefined) => {
    if (!timeStamp) return 'N/A';

    const momentTime = moment(timeStamp).local();
    const timezone = moment.tz(moment.tz.guess()).zoneAbbr();

    return `${moment(momentTime).format('MMM DD, hh:mmA')} (${timezone})`;
  };

  return (
    <Tooltip.Provider delayDuration={0}>
      <Tooltip>
        <Tooltip.Trigger asChild>
          <div className={cn('tw-absolute', { 'tw-ml-12': !isEmptyType })}>
            <StatusIndicatorIcon type={status} />
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content
          className="tw-bg-white tw-shadow-md tw-rounded tw-py-2 tw-px-4 tw-text-center tw-z-9999"
          disabled={isEmptyType}
          side="right"
          sideOffset={2}
        >
          <div className="tw-flex tw-flex-col tw-text-sm">
            <div className="tw-flex tw-items-center">
              <span className="tw-mr-2">Status:</span>
              <StatusIndicatorIcon className="tw-mr-1" type={status} />
              <span>{STATUS_INFO[status].label}</span>
            </div>

            <div className="tw-flex tw-items-center">
              <span className="tw-mr-2">Turned in:</span>
              <span className={cn({ 'tw-text-med-grey': !submission?.submitted_at })}>
                {formatTime(submission?.submitted_at)}
              </span>
            </div>

            <div className="tw-flex tw-items-center">
              <span className="tw-mr-2">Last exported on:</span>
              <span className={cn({ 'tw-text-med-grey': !submission?.lms_synced_at })}>
                {formatTime(submission?.lms_synced_at)}
              </span>
            </div>
          </div>

          <Tooltip.Arrow className="tw-fill-white tw-bg-transparent" />
        </Tooltip.Content>
      </Tooltip>
    </Tooltip.Provider>
  );
};

export default StatusIndicator;
